import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TableContainer,
  Paper,
} from "@mui/material";

// --- 1. Data Structure ---
const initialData = [
  { id: 1, name: "John Doe", details: { city: "New York", age: 30 } },
  { id: 2, name: "Jane Smith", details: { city: "London", age: 25 } },
  { id: 3, name: "Peter Jones", details: { city: "New York", age: 35 } },
  { id: 4, name: "Mike Ross", details: { city: "Boston", age: 40 } },
];

// --- 2. Helper Functions for Sorting ---

/**
 * Helper to get nested value from an object using a string path (e.g., 'details.city')
 * @param {object} obj - The source object.
 * @param {string} path - The dot-separated path to the nested property.
 * @returns {*} The value at the nested path.
 */
const getNestedValue = (obj, path) => {
  return path.split(".").reduce((acc, part) => acc && acc[part], obj);
};

/**
 * A standard descending comparator function.
 * @param {*} a Value A
 * @param {*} b Value B
 * @param {string} orderBy The property path to order by
 */
function descendingComparator(a, b, orderBy) {
  const valA = getNestedValue(a, orderBy);
  const valB = getNestedValue(b, orderBy);

  if (valB < valA) {
    return -1;
  }
  if (valB > valA) {
    return 1;
  }
  return 0;
}

/**
 * Get the comparator function based on sort order ('asc' or 'desc') and property path.
 * @param {'asc' | 'desc'} order The sort order.
 * @param {string} orderBy The property path to order by.
 */
function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

/**
 * Sorts an array using a stable sort algorithm (maintains original order for equal elements).
 * @param {Array} array The array to sort.
 * @param {Function} comparator The comparison function.
 */
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1]; // Maintain stability
  });
  return stabilizedThis.map((el) => el[0]);
}

// --- 3. The Main Table Component ---

const headCells = [
  { id: "id", label: "id" },
  { id: "organizationName", label: "Название" }, // Use the nested key path here
  { id: "subscription.plan.name", label: "Тариф" }, // Use the nested key path here
];

const MUITableWithNestedSort = () => {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name"); // Default sort key

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Memoize the sorted data to prevent unnecessary re-sorting on every render
  const sortedData = useMemo(
    () => stableSort(initialData, getComparator(order, orderBy)),
    [order, orderBy]
  );

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }} aria-label="simple table with sorting">
        <TableHead>
          <TableRow>
            {headCells.map((headCell) => (
              <TableCell
                key={headCell.id}
                sortDirection={orderBy === headCell.id ? order : false}
              >
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : "asc"}
                  onClick={(event) => handleRequestSort(event, headCell.id)}
                >
                  {headCell.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedData.map((row) => (
            <TableRow key={row.id}>
              {/* Accessing data for display */}
              <TableCell align="left" component="th" scope="row">
                {row.name}
              </TableCell>
              <TableCell>{row.organizationName}</TableCell>
              <TableCell>{row.details.age}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Export the component for use in your application
export default MUITableWithNestedSort;
